import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ascend, compose, prop, sort } from 'ramda';
import Loading from '~~elements/Loading';
import { UserResource } from '~~apis/resource';
import UserProfile from './components/UserProfile';
import TeamProfile from './components/TeamProfile';
import TeamSeasonProfile from './components/TeamSeasonProfile';
import TeamPermission from './components/TeamPermission';
import FirstBasePermission from './components/FirstBasePermission';
import FirstBaseSubscription from './components/FirstBaseSubscription';
import LeaguePermission from './components/LeaguePermission';
import OrganizerTournamentManagement from './components/OrganizerTournamentManagement';
import { StyledDiv } from './Styles';
import OrganizerProfile from './components/OrganizerProfile';
import PlayerProfile from './components/PlayerProfile';
import PlayerPermission from './components/PlayerPermission';

const sectionMapping = {
  user_profile: {
    sidebar: '個人資料',
    title: '個人資料',
  },
  player_profile: {
    sidebar: '球員資料',
    title: '球員資料',
  },
  player_permission: {
    sidebar: '權限管理',
    title: '球員 權限管理',
  },
  team_profile: {
    sidebar: '球隊資料',
    title: '球隊資料',
  },
  team_season_profile: {
    sidebar: '賽事資料',
    title: '球隊 賽事資料',
  },
  team_permission: {
    sidebar: '權限管理',
    title: '球隊 權限管理',
  },
  firstbase_permission: {
    sidebar: '權限管理',
    title: 'First Base 權限管理',
  },
  firstbase_subscription: {
    sidebar: '訂閱內容',
    title: 'First Base 訂閱內容',
  },
  organizer_profile: {
    sidebar: '主辦資訊',
    title: '主辦資訊',
  },
  organizer_tournament_management: {
    sidebar: '賽事管理',
    title: '主辦方 賽事管理',
  },
  league_player_review: {
    sidebar: '球員審核',
    title: '主辦方 球員審核',
  },
  league_permission: {
    sidebar: '權限管理',
    title: '主辦方 權限管理',
  },
};

const getSection = (userInfo, players, teams, firstBases, leagues, section, reload) => {
  if (!userInfo) {
    return <></>;
  }

  switch (section) {
    case 'user_profile':
      return <UserProfile userInfo={userInfo} />;
    case 'player_profile':
      return <PlayerProfile players={players} reload={reload} />;
    case 'player_permission':
      return <PlayerPermission userInfo={userInfo} players={players} reload={reload} />;
    case 'team_profile':
      return <TeamProfile teams={teams} reload={reload} />;
    case 'team_season_profile':
      return <TeamSeasonProfile teams={teams} reload={reload} />;
    case 'team_permission':
      return <TeamPermission userInfo={userInfo} teams={teams} reload={reload} />;
    case 'firstbase_permission':
      return <FirstBasePermission userInfo={userInfo} firstBases={firstBases} reload={reload} />;
    case 'firstbase_subscription':
      return <FirstBaseSubscription firstBases={firstBases} />;
    case 'organizer_profile':
      return <OrganizerProfile userInfo={userInfo} leagues={leagues} reload={reload} />;
    case 'organizer_tournament_management':
      return <OrganizerTournamentManagement userInfo={userInfo} leagues={leagues} reload={reload} />;
    case 'league_permission':
      return <LeaguePermission userInfo={userInfo} leagues={leagues} reload={reload} />;
    default:
      return <></>;
  }
};

const Settings = () => {
  const { section } = useParams();
  const [userInfo, setUserInfo] = useState({});
  const [firstBases, setFirstBases] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [apiRequest, setApiRequest] = useState(2);
  const [reloadCounter, setReloadCounter] = useState(0);

  useEffect(() => {
    setApiRequest(2);
    UserResource.userInfo().then(({ data }) => {
      setUserInfo(data);
      setApiRequest((preState) => preState - 1);
    });

    UserResource.permissionList().then(({ data }) => {
      const nameAscend = ascend(compose(prop('name'), prop('info')));
      setFirstBases(sort(nameAscend, data.first_bases));
      setLeagues(sort(nameAscend, data.leagues));
      setTeams(sort(nameAscend, data.teams));
      setPlayers(sort(nameAscend, data.players));
      setApiRequest((preState) => preState - 1);
    });
  }, [reloadCounter]);

  useEffect(() => {
    const sectionEl = document.getElementById('setting-display-section');
    if (!sectionEl) {
      return;
    }
    sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [section]);

  const sectionLink = (target) => {
    return (
      <Link
        className={`section-list--item ${section === target && 'selected'}`}
        to={(location) => ({ ...location, pathname: `/settings/${target}` })}
      >
        {sectionMapping[target].sidebar}
      </Link>
    );
  };

  const reload = () => {
    setReloadCounter(reloadCounter + 1);
  };

  return (
    <StyledDiv>
      <div className="greet">
        {userInfo.avatar_url ? (
          <img className="greet--pic" src={userInfo.avatar_url} alt="" />
        ) : (
          <div className="greet--pic default"></div>
        )}
        <div className="greet--name">{userInfo.first_name}</div>
      </div>
      <div className="panel">
        <div className="section-list">
          <div className="section-list--title">個人帳戶</div>
          {sectionLink('user_profile')}
          <a className="section-list--item" href={`${process.env.REACT_APP_MAINPAGE_URL}/member`}>
            訂閱會員
          </a>
          <a className="section-list--item highlight" href={`${process.env.REACT_APP_MEMBER_URL}/user/promos`}>
            兌換會員折扣碼
          </a>
          {/* <Link className="section-list--item" to="/myorders/plugin">
            擴充套件訂單
          </Link> */}
          {players.length > 0 && (
            <>
              <div className="section-list--title">球員</div>
              {sectionLink('player_profile')}
              {sectionLink('player_permission')}
            </>
          )}
          <div className="section-list--title">球隊</div>
          {teams.length > 0 && (
            <>
              {sectionLink('team_profile')}
              {sectionLink('team_season_profile')}
              {sectionLink('team_permission')}
            </>
          )}
          <Link className="section-list--item" to="/myregistries/tournament">
            我的報名資料
          </Link>
          {/* <div className="section-list--title">First Base</div>
          {firstBases.length > 0 && sectionLink('firstbase_permission')}
          {sectionLink('firstbase_subscription')} */}
          {leagues.length > 0 && (
            <>
              <div className="section-list--title">主辦方</div>
              {sectionLink('organizer_profile')}
              {sectionLink('organizer_tournament_management')}
              {/* {sectionLink('league_player_review')} */}
              {sectionLink('league_permission')}
            </>
          )}
        </div>
        <div className="section" id="setting-display-section">
          <div className="section--title">{sectionMapping[section].title}</div>
          {apiRequest > 0 ? (
            <div className="section--loading">
              <Loading width="5rem" />
            </div>
          ) : (
            getSection(userInfo, players, teams, firstBases, leagues, section, reload)
          )}
        </div>
      </div>
    </StyledDiv>
  );
};

export default Settings;
